<script setup lang="ts">
// GGE's knob sections for the shared FundBacktestPanel (option C,
// 2026-10-11) — moved verbatim out of GgeBacktestPanel.vue (including its
// SELL/BUY label nesting). Edits the panel's reactive `params` in place.
import type { BacktestParams } from '../../api/client'

defineProps<{ params: BacktestParams; bandError: string | null }>()
</script>

<template>
  <section class="params-section">
    <h3>Data Window</h3>
    <label class="checkbox-label">
      <input type="checkbox" v-model="params.guinnessEraOnly" data-testid="guinness-era-checkbox" />
      Guinness-era only (2019-08-01+)
      <span class="knob-hint">clamps to the WS Guinness-managed era; pre-2019 rows are the Artemis-era track on the same ISIN — different manager/portfolio, quoted as mechanism check only. Off = full ws_full window (drags Sharpe ~1.46; the study's Sharpe 2.18 / CPCV 2.23 / CAGR 47.7% baseline is with this ON).</span>
    </label>
  </section>

  <section class="params-section">
    <h3>Decision driver (CL 08:40 futures rule)</h3>
    <p class="section-hint">
      WS Guinness Global Energy strikes NAV at 12:00 UK against its ~65% North-American book
      marked at the previous 16:00-ET closes. The 08:40-London CL (NYMEX WTI) print measures
      exactly the US-session information the strike has not yet absorbed. "Hold unless futures down"
      holds the fund when <em>m<sub>0840</sub> ≥ SELL bar</em> and sells to cash otherwise (the validated
      production rule; bar tuned to 0 by TPE 2026-08-18). "Fund strategy, futures can force SELL"
      layers the futures rule as a SELL-override on top of another strategy — inert for the shipped preset.
    </p>
    <label>
      Decision driver:
      <select v-model="params.stepAsideMode" data-testid="step-aside-mode-select">
        <option value="off">No futures rule</option>
        <option value="pure">Hold unless futures down</option>
        <option value="gate">Fund strategy, futures can force SELL</option>
      </select>
    </label>
    <template v-if="params.stepAsideMode !== 'off'">
      <label>Futures drop to trigger SELL (bps):
        <input type="number" v-model.number="params.stepAsideThresholdBps" data-testid="step-aside-threshold-input" />
        <!-- 2026-09-15 dead-zone rule: empty = legacy one-sided, any number opts in (must be >= SELL). -->
        <label title="Empty = legacy one-sided rule. Any number opts in: prints in [SELL, BUY]bps hold current allocation. Must be >= SELL threshold.">Futures rise to trigger BUY (bps):
          <input type="number"
                 placeholder="(none = one-sided)"
                 :value="params.stepAsideBuyThresholdBps ?? ''"
                 @input="e => { const v = (e.target as HTMLInputElement).value; params.stepAsideBuyThresholdBps = v === '' ? null : Number(v) }"
                 data-testid="step-aside-buy-threshold-input" />
        </label>
        <div v-if="bandError" class="error-hint" data-testid="step-aside-band-error">{{ bandError }}</div>
      </label>
      <!-- step_aside_reentry_bps retired 2026-09-02 (spec 2026-09-02-flicker-band-detector §5): engine ignores it; input removed. -->
    </template>
  </section>
</template>

<style scoped src="./fundBacktestKnobs.css"></style>
